/**
 * Platform Login Activity Logs — Super Admin view.
 *
 *  ONE ROW PER ORGANISATION, but laid out exactly like the Company
 *  Admin per-user table (same chips, same column shape, same chrome).
 *  Each row aggregates the tenant's users into the same fields a
 *  single member would expose:
 *
 *   User Name    → Organisation name (avatar = org initials)
 *   Role         → Plan tier
 *   Login Time   → The representative session — the most-recently-logged-in
 *                  user's OWN login (never a cross-pairing of one user's
 *                  login with another user's logout, which the old
 *                  MAX(login)+MAX(logout) aggregate produced).
 *   Logout Time  → THAT SAME user's logout, shown only when it pairs as a
 *                  same-day session (else "Not logged out"). Resolved on the
 *                  client in the display timezone so Login and Logout always
 *                  fall on the same rendered date.
 *   Status       → Online if any user online, else Offline
 *   Last Activity→ Most recent activity across the org
 *   IP Address   → IP of that representative (most-recent) login
 *
 *  Backed by `GET /super-admin/platform/login-activity`.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ShieldCheck, AlertTriangle, Building2, Activity, CircleSlash } from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { formatDateTime, formatNumber } from '../../lib/format'
import { resolveSessionPair } from '../../lib/login-activity'
import { platformOrgActivityResponse, plansResponse } from '../../lib/types'
import { useAuthStore } from '../../stores/authStore'
import { Breadcrumb } from '../../components/Breadcrumb'
import { DataTable } from '../../components/table/DataTable'
import { FilterRow } from '../../components/filters/FilterRow'

// Login cell — the representative user's login, rendered with the shared
// `formatDateTime` (DD/MM/YYYY HH:MM AM/PM in the display timezone) so this
// table matches every other timestamp in the app.
function loginLabel(r) {
  return r.lastLoginAt ? formatDateTime(r.lastLoginAt) : '—'
}

// The logout time to SHOW (and sort/export by). An org that is currently
// ONLINE has an ACTIVE session, so it has NO completed logout — a paired logout
// would be from a PRIOR session and reads as a contradiction next to the
// "Online" badge. Otherwise it's the SAME-DAY session pair (see
// `resolveSessionPair`); an unpaired / next-day logout counts as none.
function displayLogoutAt(r) {
  if (!r.lastLoginAt || r.status === 'online') return null
  return resolveSessionPair(r.lastLoginAt, r.lastLogoutAt).logoutAt ?? null
}

// Logout cell — the completed logout for the login's coherent session, or "Not
// logged out" when the user is still signed in / currently online, or "—" with
// no login at all. Login and Logout always describe ONE coherent session.
function logoutLabel(r) {
  if (!r.lastLoginAt) return '—'
  const logoutAt = displayLogoutAt(r)
  return logoutAt ? formatDateTime(logoutAt) : 'Not logged out'
}

function fmtRel(iso) {
  if (!iso) return 'never'
  try {
    const ms = Date.now() - new Date(iso).valueOf()
    if (Number.isNaN(ms) || ms < 0) return '—'
    const sec = Math.floor(ms / 1000)
    if (sec < 10) return 'just now'
    if (sec < 60) return `${sec} sec ago`
    const min = Math.floor(sec / 60)
    if (min < 60) return `${min} min ago`
    const hr = Math.floor(min / 60)
    if (hr < 24) return `${hr} hr ago`
    const day = Math.floor(hr / 24)
    if (day === 1) return 'yesterday'
    if (day < 7) return `${day} days ago`
    return formatDateTime(iso)
  } catch {
    return '—'
  }
}

export function PlatformLoginActivityPage() {
  const [statusFilter, setStatusFilter] = useState('') // '' | 'online' | 'offline'
  // Presence and failed-sign-ins are ORTHOGONAL, so they get their own
  // filter rather than sharing one dropdown. An organisation can be online
  // AND have failures, and the API models them as two params for exactly
  // that reason (see platform.routes.js GET /login-activity). Folding them
  // together also made "online orgs WITH failures" — the most useful
  // security query on this page — impossible to express.
  const [failedOnly, setFailedOnly] = useState(false)
  const [search, setSearch] = useState('')
  const authStatus = useAuthStore((s) => s.status)
  const isAuthed = authStatus === 'authenticated'

  const query = useQuery({
    queryKey: ['platform', 'org-login-activity', { statusFilter, failedOnly, search }],
    queryFn: async () => {
      const res = await api.get('/super-admin/platform/login-activity', {
        params: {
          status: statusFilter || undefined,
          failed: failedOnly ? 'true' : undefined,
          search: search.trim() || undefined,
        },
      })
      return platformOrgActivityResponse.parse(res.data)
    },
    enabled: isAuthed,
    refetchInterval: isAuthed ? 30_000 : false,
    refetchOnWindowFocus: isAuthed,
  })

  // Plan catalogue — resolves each tenant's raw `planTier` code to its
  // human plan name so the badge reads "Free Trial", not the code.
  // Shares the `platform-plans-dropdown` cache with the Storage / AI
  // Usage pages so the same PlanPill renders identically everywhere.
  const plansQuery = useQuery({
    queryKey: ['platform-plans-dropdown'],
    staleTime: 5 * 60_000,
    enabled: isAuthed,
    queryFn: async () => {
      const res = await api.get('/super-admin/plans')
      return plansResponse.parse(res.data)
    },
  })
  const planNameByCode = useMemo(() => {
    const map = new Map()
    for (const p of plansQuery.data?.data ?? []) map.set(p.code, p.name)
    return map
  }, [plansQuery.data])

  const rawRows = useMemo(() => query.data?.data ?? [], [query.data])

  // KPI tiles track the CURRENTLY-FILTERED result set (search + status) so
  // they never contradict the table below; the platform-wide total stays in
  // the "Showing X of N" footer.
  //
  // `failed` sums each organisation's failedCount — the same number the
  // per-row "X failed" badge exposes — so the tile equals the sum of the
  // badges the user sees. Counting orgs-with-any-failure instead would
  // under-report whenever one tenant has multiple failed accounts (bug
  // 0016616).
  const kpi = useMemo(() => {
    let online = 0
    let offline = 0
    let failed = 0
    for (const r of rawRows) {
      if (r.status === 'online') online += 1
      else offline += 1
      failed += r.failedCount || 0
    }
    return { all: rawRows.length, online, offline, failed }
  }, [rawRows])

  // The query returns every org (no server pagination); DataTable sorts +
  // paginates the set client-side, and the KPI strip reads `rawRows`.
  const rows = rawRows

  function clearFilters() {
    setStatusFilter('')
    setFailedOnly(false)
    setSearch('')
  }

  const hasAnyFilter = statusFilter !== '' || failedOnly || search.trim() !== ''

  const columns = useMemo(
    () => [
      {
        key: 'tenantName',
        header: 'Company Name',
        grow: true,
        pinnable: true,
        minWidth: 220,
        sortAccessor: (r) => (r.tenantName ?? '').toLowerCase(),
        exportValue: (r) => r.tenantName ?? '',
        render: (r) => (
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="font-medium text-[color:var(--color-ink-strong)] truncate">
              {r.tenantName}
            </span>
            {r.recentlyFailed ? (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-50 text-red-700 border border-red-200 dark:bg-white/[0.06] dark:text-red-300 dark:border-white/10 shrink-0"
                title={`${r.failedCount} account${
                  r.failedCount === 1 ? '' : 's'
                } with failed sign-in attempts in the last 24h`}
              >
                <AlertTriangle size={9} />
                {r.failedCount} failed
              </span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'planTier',
        header: 'Plan',
        minWidth: 130,
        sortAccessor: (r) => (r.planTier ?? '').toLowerCase(),
        exportValue: (r) => planNameByCode.get(r.planTier) ?? r.planTier ?? '',
        render: (r) =>
          r.planTier ? (
            <span className="inline-flex text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]">
              {planNameByCode.get(r.planTier) ?? r.planTier}
            </span>
          ) : (
            <span className="text-[color:var(--color-ink-subtle)]">—</span>
          ),
      },
      {
        key: 'lastLoginAt',
        header: 'Login Time',
        minWidth: 160,
        sortAccessor: (r) => (r.lastLoginAt ? new Date(r.lastLoginAt) : null),
        exportValue: (r) => loginLabel(r),
        cellClassName:
          'text-[color:var(--color-ink)] tabular-nums whitespace-nowrap',
        render: (r) => loginLabel(r),
      },
      {
        key: 'lastLogoutAt',
        header: 'Logout Time',
        minWidth: 160,
        // Sort by the DISPLAYED logout (what the cell renders) so ordering
        // and display never disagree — online / unpaired sorts as blank.
        sortAccessor: (r) => {
          const at = displayLogoutAt(r)
          return at ? new Date(at) : null
        },
        exportValue: (r) => logoutLabel(r),
        cellClassName:
          'text-[color:var(--color-ink)] tabular-nums whitespace-nowrap',
        render: (r) => logoutLabel(r),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 120,
        sortAccessor: (r) => (r.status === 'online' ? 0 : 1),
        exportValue: (r) => r.status,
        render: (r) =>
          r.status === 'online' ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold border border-emerald-200 dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10">
              <span
                className="w-1.5 h-1.5 rounded-full bg-emerald-500"
                style={{
                  boxShadow: '0 0 0 2px rgba(34,197,94,0.25)',
                  animation: 'pulse 2s ease-in-out infinite',
                }}
              />
              Online
              {r.onlineCount > 1 ? (
                <span className="ml-0.5 text-[10px] opacity-70">
                  ({r.onlineCount})
                </span>
              ) : null}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold dark:bg-white/[0.06] dark:text-slate-300">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
              Offline
            </span>
          ),
      },
      {
        key: 'lastActiveAt',
        header: 'Last Activity',
        minWidth: 140,
        sortAccessor: (r) => (r.lastActiveAt ? new Date(r.lastActiveAt) : null),
        exportValue: (r) =>
          r.lastActiveAt ? formatDateTime(r.lastActiveAt) : '',
        cellClassName:
          'text-[color:var(--color-ink-muted)] whitespace-nowrap',
        render: (r) => fmtRel(r.lastActiveAt),
      },
      {
        key: 'lastSignInIp',
        header: 'IP Address',
        minWidth: 130,
        sortable: false,
        exportValue: (r) => r.lastSignInIp ?? '',
        cellClassName:
          'text-[12px] text-[color:var(--color-ink-muted)] font-mono whitespace-nowrap',
        render: (r) => r.lastSignInIp || '—',
      },
    ],
    [planNameByCode],
  )

  return (
    <div>
      <Breadcrumb current="Login Activity Logs" homePath="/super-admin/dashboard" />

      <div className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <Activity size={22} className="text-[color:var(--color-accent-strong)]" />
          Login Activity Logs
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          Sign-in activity rolled up by company — one row each.
        </p>
      </div>

      {/* KPI strip — read-only stat tiles using the platform KpiCard
       *  shape (rounded-xl chrome, halo bleed top-right, tinted icon
       *  chip) so this page reads in the same KPI vocabulary as
       *  Companies, All Users and Plans. Figures track the CURRENTLY-
       *  FILTERED result set (search + status) so they never contradict
       *  the table below; the platform-wide total stays in the footer. */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard
          label="Companies"
          value={kpi.all}
          icon={Building2}
          tone="violet"
          loading={query.isLoading}
        />
        <KpiCard
          label="Online Now"
          value={kpi.online}
          icon={Activity}
          tone="emerald"
          loading={query.isLoading}
        />
        <KpiCard
          label="Offline"
          value={kpi.offline}
          icon={CircleSlash}
          tone="slate"
          loading={query.isLoading}
        />
        <KpiCard
          label="Failed Attempts"
          value={kpi.failed}
          icon={AlertTriangle}
          tone="rose"
          loading={query.isLoading}
        />
      </div>

      {/* Toolbar — shared FilterRow primitive. Search by organisation
       *  name, a Status select (replacing the old clickable count
       *  chips), and a Clear that appears only when a filter is active.
       *  The table auto-refreshes every 30 seconds. */}
      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={setSearch}
          placeholder="Search by Company Name"
        />
        <FilterRow.Select
          ariaLabel="Status"
          placeholder="All statuses"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: '', label: 'All statuses' },
            { value: 'online', label: 'Online' },
            { value: 'offline', label: 'Offline' },
          ]}
        />
        {/* Separate from Status on purpose — "Failed attempts" is a rolling
          *  24h security tally, not a presence state, and listing it among
          *  the statuses made it read as a third mutually-exclusive value.
          *  As its own filter it composes with Status, so "Online + With
          *  failed attempts" is now expressible. */}
        <FilterRow.Select
          ariaLabel="Sign-in failures"
          placeholder="All sign-ins"
          value={failedOnly ? 'failed' : ''}
          onChange={(v) => setFailedOnly(v === 'failed')}
          options={[
            { value: '', label: 'All sign-ins' },
            { value: 'failed', label: 'With failed attempts' },
          ]}
        />
        <FilterRow.Spacer />
        <FilterRow.Clear show={hasAnyFilter} onClick={clearFilters} />
      </FilterRow>

      {/* Clarify that "failed attempts" is a security filter, not a presence
       *  status — the Status column keeps showing Online/Offline for these
       *  rows, which reads as a contradiction without this note. Mirrors the
       *  Company Admin login-activity page, which already carried it. */}
      {failedOnly ? (
        <div className="mb-4 rounded-lg border border-[color:var(--color-info-border)] bg-[color:var(--color-info-bg)] px-4 py-2.5 text-sm text-[color:var(--color-ink-strong)]">
          Showing organisations with a failed sign-in in the last 24 hours,
          tagged beside the name. The Status column still reflects each
          organisation&apos;s current online/offline presence.
        </div>
      ) : null}

      {/* Client-paginated org activity on the shared DataTable — Search +
       *  Status filters and the KPI strip above stay page-owned; read-only,
       *  so no row actions. Auto-refreshes every 30s (query refetchInterval). */}
      <DataTable
        tableId="platform-login-activity"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.tenantId}
        loading={query.isLoading}
        fetching={query.isFetching}
        error={query.isError ? extractApiError(query.error).message : null}
        onRetry={() => query.refetch()}
        searchable={false}
        title="Login Activity Logs"
        exportBaseName="org-login-activity"
        exportTitle="Login Activity Logs"
        defaultSort={[{ key: 'lastActiveAt', dir: 'desc' }]}
        emptyState={{
          icon: ShieldCheck,
          title: hasAnyFilter
            ? 'No companies match these filters.'
            : 'No companies yet.',
        }}
      />

      <p className="mt-6 text-center text-xs text-[color:var(--color-ink-subtle)]">
        Auto-refreshes every 30 seconds.
      </p>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.85); }
        }
      `}</style>
    </div>
  )
}

// Tone palette mirrors the Companies / All Users KpiCard tokens so the
// platform-admin KPI strips share one colour vocabulary. `slate` is the
// muted tile used for the neutral "Offline" count.
const KPI_TONES = {
  violet: {
    tile: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
    halo: 'from-[#EDE9FE] to-transparent dark:from-transparent',
  },
  emerald: {
    tile: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
    halo: 'from-[#D1FAE5] to-transparent dark:from-transparent',
  },
  rose: {
    tile: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10',
    halo: 'from-[#FFE4E6] to-transparent dark:from-transparent',
  },
  slate: {
    tile: 'bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0] dark:bg-white/[0.06] dark:text-slate-300 dark:border-white/10',
    halo: 'from-[#F1F5F9] to-transparent dark:from-transparent',
  },
}

/** Single-stat KPI card matching the Companies / All Users shape —
 *  rounded-xl chrome, blurred halo bleed top-right, 12×12 tinted icon
 *  chip, font-display value, uppercase tracking-wider label. Skeleton
 *  state during the initial fetch swaps the number for a pulsing
 *  placeholder so the strip doesn't pop in. */
function KpiCard({ label, value, icon: Icon, tone = 'violet', loading }) {
  const tones = KPI_TONES[tone] ?? KPI_TONES.violet
  return (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 min-h-[120px]">
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0">
            {label}
          </h3>
          {loading ? (
            <div className="mt-3 h-7 w-14 rounded animate-pulse bg-[color:var(--color-rule)]" />
          ) : (
            <p className="mt-2 font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
              {formatNumber(Number(value ?? 0))}
            </p>
          )}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            tones.tile
          }
        >
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}
